/**
 * ManageCustomFieldsDialog
 *
 * Schema management for all custom fields. Lets admins:
 *   - Rename a field
 *   - For `select` fields: add, rename, or delete the curated OPTIONS that
 *     live on the field definition
 *   - Delete a field globally
 *
 * Definitions are account-scoped reference data (the lookups slice); every
 * mutation dispatches through it, so the dialog (and every other consumer)
 * re-renders off the updated store. Option edits change only the
 * definition's option list — values already saved on a course aren't
 * rewritten, so a course keeps an old value until it's re-saved.
 */

import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '../ui';
import { AddIcon, CloseIcon, DeleteIcon, EditIcon } from '../icons';
import {
  CourseCustomField,
  CourseCustomFieldType,
  SaveCourseCustomFieldRequest,
} from '../../apis/api-client';
import {
  deleteCustomField,
  selectAllCustomFields,
  updateCustomField,
  useEnsureCustomFields,
} from '../../features/lookups/lookupsSlice';
import { useAppDispatch } from '../../stores';

interface ManageCustomFieldsDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Editable chip — click the label to enter rename mode (inline TextField);
 * X clears the chip via the supplied delete callback.
 */
function EditableChip({
  label,
  onRename,
  onDelete,
}: {
  label: string;
  onRename: (next: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);

  if (editing) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          value={draft}
          autoFocus
          size="small"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onRename(draft);
              setEditing(false);
            }
            if (e.key === 'Escape') {
              setDraft(label);
              setEditing(false);
            }
          }}
          onBlur={() => {
            if (draft.trim() && draft !== label) onRename(draft);
            setEditing(false);
          }}
          sx={{ minWidth: 140, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' } }}
        />
      </Box>
    );
  }

  return (
    <Chip
      label={label}
      size="small"
      onClick={() => {
        setDraft(label);
        setEditing(true);
      }}
      onDelete={onDelete}
      sx={{ cursor: 'pointer' }}
    />
  );
}

export default function ManageCustomFieldsDialog({ open, onClose }: ManageCustomFieldsDialogProps) {
  const dispatch = useAppDispatch();
  useEnsureCustomFields();
  const definitions = useSelector(selectAllCustomFields);

  const [editingNameId, setEditingNameId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState('');
  const [optionDraft, setOptionDraft] = useState<Record<number, string>>({});

  // All schema writes go through a full-replace save so one request carries
  // the complete definition (the update endpoint replaces, like the others).
  const saveField = (def: CourseCustomField, patch: Partial<SaveCourseCustomFieldRequest>) => {
    if (!def.id) return;
    dispatch(
      updateCustomField({
        id: def.id,
        request: {
          name: def.name,
          fieldType: def.fieldType,
          options: def.options,
          sequenceOrder: def.sequenceOrder,
          ...patch,
        },
      }),
    );
  };

  const startRename = (def: CourseCustomField) => {
    setEditingNameId(def.id ?? null);
    setDraftName(def.name ?? '');
  };
  const commitRename = (def: CourseCustomField) => {
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === def.name) {
      setEditingNameId(null);
      return;
    }
    saveField(def, { name: trimmed });
    setEditingNameId(null);
  };

  const handleAddOption = (def: CourseCustomField) => {
    const draft = (optionDraft[def.id!] || '').trim();
    if (!draft) return;
    const opts = def.options ?? [];
    if (!opts.some((o) => o.toLowerCase() === draft.toLowerCase())) {
      saveField(def, { options: [...opts, draft] });
    }
    setOptionDraft((prev) => ({ ...prev, [def.id!]: '' }));
  };
  const handleRenameOption = (def: CourseCustomField, oldName: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    const opts = def.options ?? [];
    // Drop the old slot and dedupe if the new name already exists.
    const next = opts
      .map((o) => (o === oldName ? trimmed : o))
      .filter((o, i, arr) => arr.findIndex((x) => x.toLowerCase() === o.toLowerCase()) === i);
    saveField(def, { options: next });
  };
  const handleRemoveOption = (def: CourseCustomField, option: string) => {
    const ok = window.confirm(
      `Remove "${option}" from "${def.name}"? Courses that already use it keep the value until they're re-saved.`,
    );
    if (!ok) return;
    saveField(def, { options: (def.options ?? []).filter((o) => o !== option) });
  };

  const handleDeleteField = (def: CourseCustomField) => {
    const ok = window.confirm(
      `Delete the "${def.name}" field? This removes it (and any saved values) from every course.`,
    );
    if (!ok || !def.id) return;
    dispatch(deleteCustomField(def.id));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Manage custom fields
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rename fields, edit a multi-select field's options, or remove a field entirely. Changes apply across all courses.
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {definitions.length === 0 ? (
          <Typography color="text.secondary">
            No custom fields defined yet. Add one from a course's Custom Fields card to get started.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {definitions.map((def) => {
              const isSelect = def.fieldType === CourseCustomFieldType.Select;
              const options = def.options ?? [];

              return (
                <Box
                  key={def.id}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {editingNameId === def.id ? (
                      <>
                        <TextField
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          size="small"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename(def);
                            if (e.key === 'Escape') setEditingNameId(null);
                          }}
                          sx={{ flex: 1 }}
                        />
                        <Button size="small" variant="contained" onClick={() => commitRename(def)}>
                          Save
                        </Button>
                        <Button size="small" onClick={() => setEditingNameId(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                          {def.name}
                        </Typography>
                        <Chip
                          label={
                            isSelect
                              ? 'Multi-select'
                              : def.fieldType === CourseCustomFieldType.Number
                              ? 'Number'
                              : 'Text'
                          }
                          size="small"
                          variant="outlined"
                        />
                        <Tooltip title="Rename field">
                          <IconButton size="small" onClick={() => startRename(def)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete field">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteField(def)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>

                  {/* Option management — multi-select fields only. Text and
                      number fields hold a free value per course, so there's
                      no curated option list to manage. */}
                  {isSelect && (
                    <>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                          {options.length > 0
                            ? 'Options available on every course · click a chip to rename it, X to remove it.'
                            : 'No options yet — add some below to surface them on every course.'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {options.map((opt) => (
                            <EditableChip
                              key={opt}
                              label={opt}
                              onRename={(next) => handleRenameOption(def, opt, next)}
                              onDelete={() => handleRemoveOption(def, opt)}
                            />
                          ))}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          placeholder="Add option (e.g. Front Desk, Dentist)"
                          value={optionDraft[def.id!] || ''}
                          onChange={(e) =>
                            setOptionDraft((prev) => ({ ...prev, [def.id!]: e.target.value }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddOption(def);
                            }
                          }}
                          sx={{ flex: 1, maxWidth: 320 }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => handleAddOption(def)}
                          disabled={!(optionDraft[def.id!] || '').trim()}
                        >
                          Add option
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
